import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '../components/AdminLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPES = [
  { key: 'all',               label: 'Breaking News (All)' },
  { key: 'education',         label: 'Education Pulse' },
  { key: 'healthcare',        label: 'Healthcare Pulse' },
  { key: 'business-finance',  label: 'Business & Finance' },
  { key: 'government',        label: 'Government Pulse' },
  { key: 'technology-ai',     label: 'Technology & AI' },
  { key: 'workforce-labor',   label: 'Workforce & Labor' },
];

const SPEED_OPTIONS = [
  { value: 'very_slow', label: 'Very Slow (70s)' },
  { value: 'slow',      label: 'Slow (40s)' },
  { value: 'medium',    label: 'Medium (25s)' },
  { value: 'fast',      label: 'Fast (15s)' },
];

const TAG_OPTIONS = ['', 'SPONSORED', 'FEATURED'];

const NAVY  = '#16304a';
const TEAL  = '#1d6f6a';
const GOLD  = '#c79a3a';
const CARD  = '#ffffff';
const MUTED = '#5b6066';

function authHeader() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ─── Pinned Item Row ───────────────────────────────────────────────────────────
function PinnedItemRow({ item, idx, onChange, onRemove }) {
  const set = (field, val) => onChange(idx, { ...item, [field]: val });
  return (
    <div style={{ border: '1px solid #e8e4db', borderRadius: 6, padding: 14, background: '#fafaf8', marginBottom: 10 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 4 }}>Text *</label>
          <input
            value={item.text || ''}
            onChange={e => set('text', e.target.value)}
            placeholder="Pinned headline text"
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d8d3c8', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
            data-testid={`pinned-text-${idx}`}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 4 }}>Link URL</label>
          <input
            value={item.link_url || ''}
            onChange={e => set('link_url', e.target.value || null)}
            placeholder="https://... (optional)"
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d8d3c8', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, alignItems: 'end' }}>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 4 }}>Position</label>
          <input
            type="number"
            value={item.position ?? ''}
            onChange={e => set('position', e.target.value ? parseInt(e.target.value, 10) : null)}
            placeholder="null = append"
            min="1"
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d8d3c8', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 4 }}>Tag</label>
          <select
            value={item.tag || ''}
            onChange={e => set('tag', e.target.value || null)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d8d3c8', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
          >
            {TAG_OPTIONS.map(t => <option key={t} value={t}>{t || 'None'}</option>)}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 4 }}>Expires At</label>
          <input
            type="datetime-local"
            value={item.ends_at ? item.ends_at.slice(0, 16) : ''}
            onChange={e => set('ends_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
            style={{ width: '100%', padding: '7px 10px', border: '1px solid #d8d3c8', borderRadius: 4, fontSize: 13, boxSizing: 'border-box' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 2 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: MUTED, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={item.active !== false}
              onChange={e => set('active', e.target.checked)}
              style={{ width: 14, height: 14 }}
              data-testid={`pinned-active-${idx}`}
            />
            Active
          </label>
          <button
            onClick={() => onRemove(idx)}
            style={{ marginLeft: 'auto', background: '#fee2e2', border: 'none', borderRadius: 4, padding: '5px 10px', color: '#991b1b', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
            data-testid={`pinned-remove-${idx}`}
          >Remove</button>
        </div>
      </div>
    </div>
  );
}

// ─── Scope Editor Panel ────────────────────────────────────────────────────────
function ScopePanel({ scopeKey }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    setError('');
    axios.get(`${API}/admin/ticker-settings/${scopeKey}`, { headers: authHeader() })
      .then(r => setSettings(r.data))
      .catch(() => setError('Failed to load settings'))
      .finally(() => setLoading(false));
  }, [scopeKey]);

  const update = (field, val) => setSettings(prev => ({ ...prev, [field]: val }));

  const updatePinned = (idx, item) => {
    setSettings(prev => {
      const arr = [...(prev.pinned_items || [])];
      arr[idx] = item;
      return { ...prev, pinned_items: arr };
    });
  };

  const removePinned = (idx) => {
    setSettings(prev => ({
      ...prev,
      pinned_items: (prev.pinned_items || []).filter((_, i) => i !== idx),
    }));
  };

  const addPinned = () => {
    setSettings(prev => ({
      ...prev,
      pinned_items: [...(prev.pinned_items || []), { text: '', link_url: null, position: null, tag: null, active: true, ends_at: null }],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await axios.put(`${API}/admin/ticker-settings/${scopeKey}`, settings, { headers: authHeader() });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      setError('Save failed — check your session.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, color: MUTED, textAlign: 'center' }}>Loading…</div>;
  if (!settings) return <div style={{ padding: 40, color: '#c0392b' }}>{error || 'Settings unavailable'}</div>;

  const inputStyle = { width: '100%', padding: '8px 11px', border: '1px solid #d8d3c8', borderRadius: 4, fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: MUTED, marginBottom: 5 };

  return (
    <div>
      {/* Enabled toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 0', borderBottom: '1px solid #ede9df', marginBottom: 20 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <div
            onClick={() => update('enabled', !settings.enabled)}
            style={{
              width: 42, height: 24, borderRadius: 12,
              background: settings.enabled ? TEAL : '#d1cec7',
              position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
              flexShrink: 0,
            }}
            data-testid={`toggle-enabled-${scopeKey}`}
          >
            <div style={{
              position: 'absolute', top: 3, left: settings.enabled ? 21 : 3,
              width: 18, height: 18, borderRadius: '50%', background: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,.2)', transition: 'left 0.2s',
            }} />
          </div>
          <span style={{ fontWeight: 600, color: settings.enabled ? TEAL : MUTED, fontSize: 14 }}>
            {settings.enabled ? 'Ticker Enabled' : 'Ticker Disabled'}
          </span>
        </label>
        <span style={{ fontSize: 12, color: MUTED }}>
          {settings.enabled ? 'This ticker is live on the public site.' : 'This ticker is hidden — page shows no bar.'}
        </span>
      </div>

      {/* Label, Speed, Item Limit */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>Label</label>
          <input value={settings.label || ''} onChange={e => update('label', e.target.value)} style={inputStyle} data-testid={`label-${scopeKey}`} />
        </div>
        <div>
          <label style={labelStyle}>Scroll Speed</label>
          <select value={settings.speed || 'medium'} onChange={e => update('speed', e.target.value)} style={inputStyle} data-testid={`speed-${scopeKey}`}>
            {SPEED_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Article Limit</label>
          <input type="number" min="1" max="20" value={settings.item_limit ?? 8} onChange={e => update('item_limit', parseInt(e.target.value, 10) || 8)} style={inputStyle} data-testid={`item-limit-${scopeKey}`} />
        </div>
      </div>

      {/* Colors + pause_on_hover */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 16, marginBottom: 20, alignItems: 'end' }}>
        {[
          { field: 'bg_color', label: 'Background Color' },
          { field: 'text_color', label: 'Text Color' },
          { field: 'accent_color', label: 'Accent / Gold Color' },
        ].map(({ field, label }) => (
          <div key={field}>
            <label style={labelStyle}>{label}</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="color" value={settings[field] || '#000'} onChange={e => update(field, e.target.value)} style={{ width: 36, height: 36, padding: 2, border: '1px solid #d8d3c8', borderRadius: 4, cursor: 'pointer' }} />
              <input value={settings[field] || ''} onChange={e => update(field, e.target.value)} style={{ ...inputStyle, flex: 1 }} />
            </div>
          </div>
        ))}
        <div>
          <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer' }}>
            <input type="checkbox" checked={settings.pause_on_hover !== false} onChange={e => update('pause_on_hover', e.target.checked)} style={{ width: 14, height: 14 }} data-testid={`pause-hover-${scopeKey}`} />
            Pause on hover
          </label>
        </div>
      </div>

      {/* Color preview */}
      <div style={{ background: settings.bg_color || '#0D2137', borderRadius: 6, padding: '10px 16px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: settings.accent_color || GOLD, flexShrink: 0 }} />
        <span style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: 13, color: settings.text_color || '#F5F0E8', letterSpacing: '0.3px' }}>{settings.label}</span>
        <span style={{ color: settings.text_color || '#F5F0E8', fontSize: 12, opacity: 0.5, marginLeft: 12 }}>— live preview of colors</span>
      </div>

      {/* Pinned Items */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: NAVY }}>Pinned Items</div>
            <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>Injected into the ticker at a fixed position or appended. Null position = append to end.</div>
          </div>
          <button
            onClick={addPinned}
            style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 4, padding: '7px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
            data-testid={`add-pinned-${scopeKey}`}
          >+ Add Item</button>
        </div>
        {(!settings.pinned_items || settings.pinned_items.length === 0) ? (
          <div style={{ padding: '18px 16px', border: '1px dashed #d8d3c8', borderRadius: 6, color: MUTED, fontSize: 13, textAlign: 'center' }}>No pinned items. Click "+ Add Item" to create one.</div>
        ) : (
          settings.pinned_items.map((item, idx) => (
            <PinnedItemRow key={idx} item={item} idx={idx} onChange={updatePinned} onRemove={removePinned} />
          ))
        )}
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingTop: 16, borderTop: '1px solid #ede9df' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ background: saving ? MUTED : NAVY, color: '#fff', border: 'none', borderRadius: 4, padding: '10px 22px', fontWeight: 700, fontSize: 14, cursor: saving ? 'default' : 'pointer' }}
          data-testid={`save-ticker-${scopeKey}`}
        >{saving ? 'Saving…' : 'Save Settings'}</button>
        {saved && <span style={{ color: '#1d7a50', fontWeight: 600, fontSize: 13 }}>Saved successfully</span>}
        {error && <span style={{ color: '#c0392b', fontSize: 13 }}>{error}</span>}
      </div>
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────
export default function AdminTickerPage() {
  const [activeScope, setActiveScope] = useState('all');

  return (
    <AdminLayout>
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 0 48px', fontFamily: "'Public Sans', sans-serif" }} data-testid="admin-ticker-page">
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontFamily: "'Newsreader', Georgia, serif", fontWeight: 500, fontSize: 32, color: NAVY, margin: '0 0 6px' }}>Ticker Controls</h1>
          <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>Manage the scrolling ticker bar for each section. Enable/disable, set label, speed, colors, and inject pinned items.</p>
        </div>

        {/* Scope tabs */}
        <div style={{ display: 'flex', borderBottom: '2px solid #e8e4db', marginBottom: 28, gap: 2, flexWrap: 'wrap' }}>
          {SCOPES.map(s => (
            <button
              key={s.key}
              onClick={() => setActiveScope(s.key)}
              style={{
                background: activeScope === s.key ? NAVY : 'transparent',
                color: activeScope === s.key ? '#fff' : MUTED,
                border: 'none',
                borderRadius: '4px 4px 0 0',
                padding: '9px 16px',
                fontWeight: 600,
                fontSize: 12.5,
                cursor: 'pointer',
                fontFamily: 'inherit',
                marginBottom: -2,
                borderBottom: activeScope === s.key ? `2px solid ${NAVY}` : '2px solid transparent',
              }}
              data-testid={`tab-${s.key}`}
            >{s.label}</button>
          ))}
        </div>

        {/* Panel */}
        <div style={{ background: CARD, border: '1px solid #e8e4db', borderRadius: 8, padding: '24px 28px' }}>
          <ScopePanel key={activeScope} scopeKey={activeScope} />
        </div>
      </div>
    </AdminLayout>
  );
}
